import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { percent } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { AuditHistoryButton, AuditLogPanel } from '@/components/AuditLog'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { PageHeader, Tabs } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, CardHeader, ErrorState, Loading } from '@/components/ui/primitives'
import { Field, FormGrid, Input, Textarea } from '@/components/ui/form'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { CountryDetail, CountryListItem, Paged } from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = 'name'

export function Countries() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const list = useAsync<Paged<CountryListItem>>(
    (signal) =>
      api.get<Paged<CountryListItem>>(
        '/countries',
        { includeInactive: status !== 'active', search, sort, page, pageSize },
        signal,
      ),
    [status, search, sort, page, pageSize],
  )

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Countries"
        hint="Each country carries its own entity details, tax, numbering and terms. A country that cannot issue quotations says why."
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'iso', label: 'ISO code' },
          { value: 'currency', label: 'Currency' },
          { value: '-status', label: 'Active first' },
        ]}
      >
        <div className="w-56">
          <Input
            placeholder="Search name or code"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next))}>
          <option value="">Active and inactive</option>
          <option value="active">Active only</option>
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            <Table>
              <thead>
                <tr>
                  <SortableTh field="iso" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                    Country
                  </SortableTh>
                  <Th>Legal entity</Th>
                  <Th>Numbering</Th>
                  <Th>Tax</Th>
                  <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                    Status
                  </SortableTh>
                  <Th>History</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((country) => (
                  <Tr key={country.id} onClick={can('country.edit') ? () => navigate(`/countries/${country.id}`) : undefined}>
                    <Td>
                      <span className="font-mono text-[12px] text-muted">{country.isoCode}</span>
                      <span className="ml-2 font-medium">{country.name}</span>
                    </Td>
                    <Td className="text-muted">{country.legalEntityName}</Td>
                    <Td className="font-mono text-[12px]">{country.quotationPrefix}</Td>
                    <Td>
                      {country.taxLabel} {percent(country.taxRate)}
                    </Td>
                    <Td>
                      {country.canIssueQuotations ? (
                        <Badge tone="positive">Ready</Badge>
                      ) : (
                        <span className="flex flex-col gap-1">
                          <Badge tone="warning">Blocked</Badge>
                          {/* The blockers are the whole point of the readiness gate: a country
                              that cannot issue quotations has to say what is missing. */}
                          <span className="text-[12px] text-muted">{country.blockers.join(' · ')}</span>
                        </span>
                      )}
                    </Td>
                    <Td>
                      <AuditHistoryButton
                        entityType="Country"
                        entityId={country.id}
                        title={country.name}
                      />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>
    </>
  )
}

/** What the editor changes, and therefore what an unsaved change can be about. */
const EDITED = [
  'name',
  'legalEntityName',
  'registrationNo',
  'taxRegistrationNo',
  'addressLines',
  'email',
  'quotationEmail',
  'website',
  'currencySymbol',
  'currencyDecimals',
  'taxLabel',
  'taxRate',
  'governingLaw',
  'defaultValidityDays',
  'discountApprovalThresholdPercent',
] as const

/** A country at its own address. See ProductDetailPage for why. */
export function CountryDetailPage() {
  const { id } = useParams()
  return id ? (
    <CountryEditor countryId={id} />
  ) : (
    <ErrorState title="No country" detail="That link is missing a country id." />
  )
}

function CountryEditor({ countryId }: { countryId: string }) {
  const navigate = useNavigate()
  const onDone = () => navigate('/countries')
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [form, setForm] = useState<Partial<CountryDetail>>({})
  const [tab, setTab] = useState<'entity' | 'money' | 'defaults' | 'history'>('entity')

  const detail = useAsync<CountryDetail>(
    (signal) => api.get<CountryDetail>(`/countries/${countryId}`, undefined, signal).then((loaded) => {
      setForm(loaded)
      return loaded
    }),
    [countryId],
  )

  const apply = useCallback((values: Partial<CountryDetail>) => setForm(values), [])
  const changes = useUnsavedChanges(form, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof CountryDetail>(key: K, value: CountryDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/countries/${countryId}`, {
        name: form.name,
        legalEntityName: form.legalEntityName,
        registrationNo: form.registrationNo,
        taxRegistrationNo: form.taxRegistrationNo,
        addressLines: form.addressLines,
        email: form.email,
        website: form.website,
        quotationEmail: form.quotationEmail,
        currencySymbol: form.currencySymbol,
        currencyDecimals: form.currencyDecimals,
        taxLabel: form.taxLabel,
        taxRate: form.taxRate,
        governingLaw: form.governingLaw,
        defaultValidityDays: form.defaultValidityDays,
        discountApprovalThresholdPercent: form.discountApprovalThresholdPercent,
      })

      // Accepted before leaving, so the guard does not ask about work that has just
      // been stored.
      changes.accept()
      toast.show(`${form.name} saved`, 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save the country', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function setActive(active: boolean) {
    setSwitching(true)
    try {
      await api.post(`/countries/${countryId}/${active ? 'activate' : 'deactivate'}`, {})
      toast.show(`${detail.data?.name ?? 'Country'} ${active ? 'activated' : 'deactivated'}`, 'success')
      detail.reload()
    } catch (cause) {
      // The 409 carries the readiness blockers in its detail, which is the whole point of
      // the gate - a vague refusal would leave somebody guessing which field is missing.
      toast.show(cause instanceof ApiError ? cause.message : 'Could not change the status', 'error')
    } finally {
      setSwitching(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title={form.name ?? 'Country'}
        hint="These fields print on the customer's document. The letterhead is built from them, so an empty address is an empty letterhead."
        // The editor's fields are spread over three tabs, so its controls live in the
        // header where they are visible from all of them - and the count with them,
        // because a change made on one tab is otherwise invisible from another.
        back={{ label: 'Back to countries', onClick: () => guard.confirm(onDone) }}
        actions={
          <>
            {changes.fields.length > 0 && (
              <span className="mr-1 text-[13px] text-muted" role="status">
                {changes.fields.length} unsaved change{changes.fields.length === 1 ? '' : 's'}
              </span>
            )}
            <Button disabled={!changes.isDirty || saving} onClick={changes.discard}>
              Discard
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!changes.isDirty}
              onClick={() => void save()}
            >
              Save country
            </Button>
          </>
        }
      />

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'entity', label: 'Legal entity' },
          { id: 'money', label: 'Money & tax' },
          { id: 'defaults', label: 'Quotation defaults' },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'entity' && detail.data && (
      <Card className="mb-5">
        <CardHeader
          title="Status"
          hint="Whether this country may trade. It sits with the legal entity because these are the fields it is judged on."
        />
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {detail.data.isActive ? (
                <Badge tone="positive">Active</Badge>
              ) : (
                <Badge tone="warning">Inactive</Badge>
              )}
              <span className="text-[13px] text-muted">
                {detail.data.isActive
                  ? 'Quotations can be raised in this country.'
                  : 'No quotation can be raised here until this country is activated.'}
              </span>
            </div>

            {/* Named, not counted. A country activated with a placeholder address would
                print that placeholder on a customer's document, so the gate says which
                field is missing rather than refusing vaguely. */}
            {detail.data.blockers.length > 0 && (
              <ul className="flex flex-col gap-1 text-[12.5px] text-muted">
                {detail.data.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            )}
          </div>

          {detail.data.isActive ? (
            <Button loading={switching} onClick={() => void setActive(false)}>
              Deactivate
            </Button>
          ) : (
            <Button
              variant="primary"
              loading={switching}
              disabled={detail.data.blockers.length > 0}
              onClick={() => void setActive(true)}
            >
              Activate
            </Button>
          )}
        </div>
      </Card>
      )}

      {tab === 'entity' && (
      <Card className="mb-5">
        <CardHeader title="Legal entity" hint="What the document says about who is issuing it." />
        <FormGrid>
          <Field label="Country name" required changed={changes.changed('name')}>
            {(id) => <Input id={id} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />}
          </Field>
          <Field label="Registered name" required changed={changes.changed('legalEntityName')}>
            {(id) => (
              <Input
                id={id}
                value={form.legalEntityName ?? ''}
                onChange={(e) => set('legalEntityName', e.target.value)}
              />
            )}
          </Field>
          <Field label="Registration number" changed={changes.changed('registrationNo')}>
            {(id) => (
              <Input
                id={id}
                value={form.registrationNo ?? ''}
                onChange={(e) => set('registrationNo', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Tax registration number"
            changed={changes.changed('taxRegistrationNo')}
            hint="Printed under the entity name."
          >
            {(id) => (
              <Input
                id={id}
                value={form.taxRegistrationNo ?? ''}
                onChange={(e) => set('taxRegistrationNo', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Registered address"
            changed={changes.changed('addressLines')}
            hint="One line per line. This is the letterhead block."
          >
            {(id) => (
              <Textarea
                id={id}
                value={form.addressLines ?? ''}
                onChange={(e) => set('addressLines', e.target.value)}
              />
            )}
          </Field>
          <div className="flex flex-col gap-4">
            <Field label="General email" changed={changes.changed('email')}>
              {(id) => <Input id={id} type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />}
            </Field>
            <Field
              label="Quotation mailbox"
              changed={changes.changed('quotationEmail')}
              hint="Where a signed quotation comes back, and the address quotations are sent from."
            >
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={form.quotationEmail ?? ''}
                  onChange={(e) => set('quotationEmail', e.target.value)}
                />
              )}
            </Field>
            <Field label="Website" changed={changes.changed('website')}>
              {(id) => <Input id={id} value={form.website ?? ''} onChange={(e) => set('website', e.target.value)} />}
            </Field>
          </div>
        </FormGrid>
      </Card>
      )}

      {tab === 'money' && (
      <Card className="mb-5">
        <CardHeader title="Money and tax" hint="What an amount prints as, and what tax is added to it." />
        <FormGrid>
          <Field label="Currency" hint="Set when the country was created and never changed.">
            {(id) => <Input id={id} value={detail.data?.currencyCode ?? ''} disabled />}
          </Field>
          <Field
            label="Display symbol"
            changed={changes.changed('currencySymbol')}
            hint="What the document prints against an amount: RM, SGD, $. Falls back to the currency code."
          >
            {(id) => (
              <Input
                id={id}
                value={form.currencySymbol ?? ''}
                onChange={(e) => set('currencySymbol', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Decimal places"
            changed={changes.changed('currencyDecimals')}
            hint="2 for MYR, SGD and PHP. 0 for IDR, which has no minor unit."
          >
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                max={4}
                value={form.currencyDecimals ?? 2}
                onChange={(e) => set('currencyDecimals', Number(e.target.value))}
              />
            )}
          </Field>
          <Field
            label="Tax label"
            changed={changes.changed('taxLabel')}
            hint="SST, GST, VAT, PPN - printed as the tax line."
          >
            {(id) => <Input id={id} value={form.taxLabel ?? ''} onChange={(e) => set('taxLabel', e.target.value)} />}
          </Field>
          <Field label="Tax rate %" changed={changes.changed('taxRate')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                value={form.taxRate ?? 0}
                onChange={(e) => set('taxRate', Number(e.target.value))}
              />
            )}
          </Field>
          <Field label="Governing law" changed={changes.changed('governingLaw')}>
            {(id) => (
              <Input id={id} value={form.governingLaw ?? ''} onChange={(e) => set('governingLaw', e.target.value)} />
            )}
          </Field>
        </FormGrid>
      </Card>
      )}

      {tab === 'defaults' && (
      <Card>
        <CardHeader title="Quotation defaults" hint="Applied to every new quotation raised in this country." />
        <FormGrid>
          <Field label="Validity (days)" changed={changes.changed('defaultValidityDays')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={1}
                value={form.defaultValidityDays ?? 30}
                onChange={(e) => set('defaultValidityDays', Number(e.target.value))}
              />
            )}
          </Field>
          <Field
            label="Discount threshold %"
            changed={changes.changed('discountApprovalThresholdPercent')}
            hint="A line discount above this adds an approver. It never skips the chain - nothing does."
          >
            {(id) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                value={form.discountApprovalThresholdPercent ?? 0}
                onChange={(e) => set('discountApprovalThresholdPercent', Number(e.target.value))}
              />
            )}
          </Field>
        </FormGrid>
      </Card>
      )}

      {tab === 'history' && (
        <AuditLogPanel entityType="Country" entityId={countryId} className="" />
      )}

      <LeaveGuard guard={guard} />
    </>
  )
}
